import AsyncStorage from '@react-native-async-storage/async-storage';
import type { ArcaEnvironment, TaxRegime } from '../domain/fiscalTypes';

const DRAFT_VERSION = 5;

export type ArcaWizardDraft = {
  version: typeof DRAFT_VERSION;
  organizationId: string;
  step: number;
  taxRegime: TaxRegime;
  emitterCuit: string;
  businessName: string;
  /** Datos del emisor que ARCA pide en el comprobante (opcionales en borradores viejos). */
  domicilioComercial?: string;
  iibb?: string;
  inicioActividades?: string;
  certPem: string;
  privateKeyPem?: string;
  /** 'generate' = CSR desde Vuelto (la clave queda server-side); 'manual' = pegar cert+key. */
  certMode?: 'generate' | 'manual';
  /** PV por sucursal (branchId → número). */
  branchPvMap: Record<string, string>;
  environment: ArcaEnvironment;
  monotributoCategory: string;
  savedAt: string;
};

const draftKey = (organizationId: string) => `arca.wizard.draft.v${DRAFT_VERSION}.${organizationId}`;

export async function loadArcaWizardDraft(organizationId: string): Promise<ArcaWizardDraft | null> {
  if (!organizationId) return null;
  try {
    const raw = await AsyncStorage.getItem(draftKey(organizationId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ArcaWizardDraft;
    if (parsed?.version !== DRAFT_VERSION) return null;
    if (parsed.privateKeyPem !== undefined) {
      delete parsed.privateKeyPem;
      await AsyncStorage.setItem(draftKey(organizationId), JSON.stringify(parsed));
    }
    return parsed;
  } catch {
    return null;
  }
}

export async function saveArcaWizardDraft(
  organizationId: string,
  draft: Omit<ArcaWizardDraft, 'version' | 'savedAt' | 'organizationId' | 'privateKeyPem'>
): Promise<void> {
  const payload: ArcaWizardDraft = {
    ...draft,
    organizationId,
    version: DRAFT_VERSION,
    savedAt: new Date().toISOString(),
  };
  await AsyncStorage.setItem(draftKey(organizationId), JSON.stringify(payload));
}

export async function clearArcaWizardDraft(organizationId: string): Promise<void> {
  if (!organizationId) return;
  await AsyncStorage.removeItem(draftKey(organizationId));
}

const STEP_COUNT = 3;

/** Restaura el paso del wizard según servidor + borrador local. */
export function resolveArcaWizardStep(params: {
  setupStatus?: 'draft' | 'validated' | 'error' | null;
  draftStep?: number | null;
  hasEmitterProfile?: boolean;
}): number {
  // La pregunta de para qué facturás es la puerta. Un borrador de esta versión
  // retoma el paso; un perfil viejo no se salta la pregunta.
  if (params.setupStatus === 'validated') return 0;
  if (params.draftStep != null && Number.isFinite(params.draftStep)) {
    return Math.min(Math.max(Math.trunc(params.draftStep), 0), STEP_COUNT - 1);
  }
  return 0;
}
